import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportsService, ReportQueryParams, ReportResponse, JobSiteProgressReport } from '../services/reports.service';
import { lookupService } from '../services/lookups.service';
import { JobsiteService } from '../services/jobsite.service';
import { authService } from '../services/auth.service';
import ExcelJS from 'exceljs';
import ReportExportButton from '../components/reports/ReportExportButton';
import { exportToPdf } from './report-details';

import Breadcrumb from '../components/breadcrumb';
import { ReportDetailsSkeleton } from '../components/Skeleton';
import JobsiteVisitsFilters from '../components/reports/JobsiteVisitsFilters';
import { FilterState } from '../components/reports/types';
import Table from '../components/table';
import { getReportColumns } from '../components/reports/ReportTableColumns';
import { Organizers, Province, Local } from '../services/lookups.service';
import { Jobsite } from '../services/jobsite.service';

// Status and union status options
const statusOptions = [
  { id: 'active', name: 'Active' },
  { id: 'completed', name: 'Completed' }
];

const unionStatusOptions = [
  { id: 'union', name: 'Union' },
  { id: 'non-union', name: 'Non-Union' },
  { id: 'mixed', name: 'Mixed' }
];

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc' | null;
}

interface JobsiteDropDownListItem {
  id: string;
  name: string;
}

const JobsiteVisits: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<JobSiteProgressReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: '', direction: null });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Get user role for permission checks
  const userRole = authService.getUser()?.role || '';

  // Filter states
  const [activeFilters, setActiveFilters] = useState<FilterState>({
    sector: [],
    province: [],
    status: [],
    startDate: '',
    endDate: '',
    search: '',
    organizer: [],
    unionStatus: [],
    jobsite: [],
    localUnion: []
  });

  // Lookup data
  const [organizers, setOrganizers] = useState<Organizers[]>([]);
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [jobsites, setJobsites] = useState<JobsiteDropDownListItem[]>([]);
  const [localUnions, setLocalUnions] = useState<{ id: string; name: string }[]>([]);

  // Load lookup data
  useEffect(() => {
    const loadLookupData = async () => {
      try {
        const [organizersData, provincesData, jobsitesData, localUnionsData] = await Promise.all([
          lookupService.getFilteredOrganizers(),
          lookupService.getProvinces(),
          JobsiteService.getAllJobsites(),
          lookupService.getLocals()
        ]);
        setOrganizers(organizersData);
        setProvinces(provincesData);
        // Convert jobsites to the format expected by the filter component
        const formattedJobsites = jobsitesData.data.map((jobsite: Jobsite) => ({
          id: jobsite.id,
          name: jobsite.jobSiteName
        }));
        setJobsites(formattedJobsites);
        // Convert local unions to the format expected by the filter component
        const formattedLocalUnions = localUnionsData.map((local: Local) => ({
          id: local.id,
          name: `Local ${local.unionNumber} - ${local.province.name}`
        }));
        setLocalUnions(formattedLocalUnions);
      } catch (err) {
        console.error('Error loading lookup data:', err);
      }
    };

    loadLookupData();
  }, []);

  // Build base query params with filters and sorting
  const buildJobsiteVisitsBaseParams = (): ReportQueryParams => {
    const params: ReportQueryParams = {
      sortBy: sortConfig.key || 'visitDate',
      sortOrder: sortConfig.direction || 'desc'
    };

    // Apply filters
    if (activeFilters.organizer && activeFilters.organizer.length > 0) {
      params.organizer = activeFilters.organizer;
    }
    if (activeFilters.province && activeFilters.province.length > 0) {
      params.province = activeFilters.province;
    }
    if (activeFilters.unionStatus && activeFilters.unionStatus.length > 0) {
      params.unionStatus = activeFilters.unionStatus;
    }
    if (activeFilters.jobsite && activeFilters.jobsite.length > 0) {
      params.jobsite = activeFilters.jobsite;
    }
    if (activeFilters.startDate) {
      params.startDate = activeFilters.startDate.split('T')[0];
    }
    if (activeFilters.endDate) {
      params.endDate = activeFilters.endDate.split('T')[0];
    }
    if (activeFilters.status && activeFilters.status.length > 0) {
      params.status = activeFilters.status;
    }
    if (activeFilters.localUnion && activeFilters.localUnion.length > 0) {
      params.localUnion = activeFilters.localUnion;
    }

    return params;
  };

  // Fetch jobsite visits data with pagination
  const fetchJobsiteVisits = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = 
      { ...buildJobsiteVisitsBaseParams(),
        page: currentPage,
        limit: pageSize,
      };

      const response: ReportResponse = await reportsService.getJobsiteVisits(params);
      setData(response.data);
      setTotalRecords(response.totalCount);
    } catch (err) {
      setError('Failed to fetch jobsite visits data');
      console.error('Error fetching jobsite visits:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch jobsite visits when filters, sorting, or pagination changes
    fetchJobsiteVisits();
  }, [sortConfig, currentPage, pageSize, activeFilters]);

  // Handle sorting
  const handleSort = (key: string, direction: 'asc' | 'desc' | null) => {
    setSortConfig({ key, direction });
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle page size change
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  // Export to Excel
  const exportToExcel = async () => {
    try {
      // Fetch all data without pagination for export
      const params = buildJobsiteVisitsBaseParams();
      const response: ReportResponse = await reportsService.getJobsiteVisits(params);
      const allData = response.data;

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Jobsite Visits');

      // Add header
      worksheet.addRow(['Organizer', 'Jobsite', 'Address', 'General Contractor', 'Completion Status', 'Province', 'Visit Date']);

      // Add data
      allData.forEach((row: JobSiteProgressReport) => {
        const formattedAddress = row.address ? `${row.address.address2 ? `${row.address.address2} - ` : ''}${row.address.address1}, ${row.address.city}, ${row.address.postalCode}` : '';
        console.log("line 198: ",row)
        worksheet.addRow([
          row.organizername,
          row.jobsite,
          formattedAddress,
          row.contractor,
          row.completionStatus,
          row.province,
          row.visitDate ? new Date(row.visitDate).toLocaleDateString('en-US') : ''
        ]);
      });

      // Style header row
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE2E2E2' }
      };
      // Auto-size columns
      worksheet.columns.forEach((column: Partial<ExcelJS.Column>) => {
        let maxLength = 0;
        if (column && column.eachCell) {
          column.eachCell({ includeEmpty: true }, (cell: ExcelJS.Cell) => {
            const columnLength = cell.value ? cell.value.toString().length : 10;
            if (columnLength > maxLength) {
              maxLength = columnLength;
            }
          });
          column.width = maxLength < 10 ? 10 : maxLength;
        }
      });

      // Save file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `jobsite-visits-${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      setError('Failed to export data to Excel');
    }
  };

  // Export to PDF
  const exportToPDF = async () => {
    try {
      // Fetch all data without pagination for export
      const params = buildJobsiteVisitsBaseParams();
      const response: ReportResponse = await reportsService.getJobsiteVisits(params);
      const allData = response.data;

      exportToPdf(allData, 'Jobsite Visits Report');
    } catch (err) {
      console.error('Error exporting to PDF:', err);
      setError('Failed to export data to PDF');
    }
  };


  if (loading) {
    return <ReportDetailsSkeleton />;
  }

  // Filter validation function
  const isJobsiteListByOrganizerFiltersValid = (): boolean => {
    return true; // Always valid for jobsite visits
  };

  // Handle apply filters
  const handleApplyFilters = () => {
    setCurrentPage(1); // Reset to first page when filters change
    // fetchJobsiteVisits will be called automatically by useEffect when activeFilters changes
  };

  // Handle clear all filters
  const handleFilterClearAll = () => {
    setActiveFilters({
      sector: [],
      province: [],
      status: [],
      startDate: '',
      endDate: '',
      search: '',
      organizer: [],
      unionStatus: [],
      jobsite: [],
      localUnion: []
    });
    // Reset sort config and page to default to trigger a new fetch
    setCurrentPage(1);
    setSortConfig({ key: '', direction: null });
  };

  return (
    <div className="bg-gray-50 p-3 sm:p-4 md:p-6 min-h-screen">
      <div className="mb-4 sm:mb-6">
        <Breadcrumb title="Jobsite Visits" />
      </div>

      {/* Filters */}
      <JobsiteVisitsFilters
        activeFilters={activeFilters}
        setActiveFilters={setActiveFilters}
        provinces={provinces}
        statusOptions={statusOptions}
        unionStatusOptions={unionStatusOptions}
        organizers={organizers}
        jobsites={jobsites}
        localUnions={localUnions}
        handleApplyFilters={handleApplyFilters}
        handleFilterClearAll={handleFilterClearAll}
        isJobsiteListByOrganizerFiltersValid={isJobsiteListByOrganizerFiltersValid}
        setLoading={setLoading}
      />

      <div className="bg-white rounded-[12px] shadow overflow-hidden">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Jobsite Visits</h1>
              <p className="text-sm text-gray-500 mt-1">
                View and export jobsite visit records with filtering options
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              {data.length === 0 ? null : <ReportExportButton onExcelExport={exportToExcel} onPdfExport={exportToPDF} />}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          {error ? (
            <div className="text-red-500 text-center p-8">{error}</div>
          ) : data.length === 0 ? (
            <div className="text-center p-8">
              <p className="text-gray-500">No jobsite visits found matching your criteria.</p>
            </div>
          ) : (
            <>
              {/* Table with built-in pagination */}
              <Table
                columns={getReportColumns('Jobsite Visits Report', userRole, navigate)}
                data={data}
                totalItems={totalRecords}
                currentPage={currentPage}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                onSort={handleSort}
                sortConfig={sortConfig}
              />
            </>
          )}
        </div>
      </div>

    </div>
  );
};

export default JobsiteVisits;